import React from 'react';
import { Box, Typography } from '@mui/material';

import { EditableOverviewItem } from './EditableOverviewItem';

import type { UUID, StoredStandard } from '@/shared/types/customStandard';
import type { OverviewItem } from '@/shared/types/overview';
import { SortableList } from '@/shared/components/SortableList';
import { type ReorderPlacement } from '@/shared/utils/reorder';

interface SortableOverviewListProps {
  items: OverviewItem[];
  canEdit: boolean;
  isUpdating?: boolean;
  onUpdate: (updatedItem: OverviewItem) => Promise<void>;
  onDelete: (index: number) => Promise<void>;
  onMove: (itemId: string, placement: ReorderPlacement) => Promise<void>;
  ownerId?: UUID;
  parentScope?: StoredStandard[];
  allowAllToggle?: boolean;
  scopeLabel?: string;
}

export const SortableOverviewList: React.FC<SortableOverviewListProps> = ({
  items,
  canEdit,
  isUpdating = false,
  onUpdate,
  onDelete,
  onMove,
  ownerId,
  parentScope,
  allowAllToggle,
  scopeLabel,
}) => {
  if (items.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No overview items yet.
      </Typography>
    );
  }

  return (
    <Box>
      <SortableList
        items={items}
        canEdit={canEdit}
        isUpdating={isUpdating}
        onMove={onMove}
        getItemLabel={(item) => `Reorder ${item.categoryName}`}
        reorderErrorMessage="Failed to reorder overview item. Please try again."
        renderItem={(item, { setNodeRef, style, controlsDisabled, index, dragHandle }) => (
          <Box
            ref={setNodeRef}
            style={style}
            sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}
          >
            {dragHandle && <Box sx={{ mt: 1 }}>{dragHandle}</Box>}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <EditableOverviewItem
                item={item}
                index={index}
                onUpdate={onUpdate}
                onDelete={onDelete}
                canEdit={canEdit}
                isUpdating={controlsDisabled}
                ownerId={ownerId}
                parentScope={parentScope}
                allowAllToggle={allowAllToggle}
                scopeLabel={scopeLabel}
              />
            </Box>
          </Box>
        )}
      />
    </Box>
  );
};
